import React, { memo, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import CMarkdown from "../chatComponents/CMarkdown";
import CTable from "../chatComponents/CTable";
import CBi from "../chatComponents/CBi";
import ChatLoading from "../chat-loading";
import { streamText } from "./const";
import CLink from "../chatComponents/CLink";
import CButton from "../chatComponents/CButton";
import CForm from "../chatComponents/CForm";
import CList from "../chatComponents/CList";
import Appraise from '../appraise';
import styles from './index.module.scss';
import CRecommend from '../chatComponents/CRecommend';
import Global from '@/utils/global';
import ReasoningProcess from '../chatComponents/reasoning-process';
// 导入 StoChart 组件
import StoChart from '../chatComponents/StoChart';
import HitKnowledge from './hitKnowledge';
import CMetricCard from '../chatComponents/CMetricCard';
import CCustomMetricCard from '../chatComponents/CCustomMetricCard';
import { useChatStore } from '@/store';
import { CnBox } from '@sto/cn-ui';
import Operation from '../operation';
import SwitchCTable, { FeeForm } from '../chatComponents/SwitchCTable'; // 可切换表格组件（政策费率功能用，已迁移到政策主应用项目里）
import CReferences from '../chatComponents/CReferences'; // 参考资料
import CTitle from '../chatComponents/CTitle';

// 定义 renderOutput 支持的类型
type OutputType = 
  | 'LOADING'
  | 'TABLE'
  | 'LLM_TABLE'
  | 'DATA_JSON'
  | 'LINK'
  | 'BUTTON'
  | 'FORM'
  | 'LIST'
  | 'QA'
  | 'RECOMMEND'
  | 'RECOMMENDTITLE'
  | 'GUESS'
  | 'LINE_CHART'
  | 'BAR_CHART'
  | 'PIE_CHART'
  | 'DOUGHNUT_CHART'
  | 'TABLE_CHART'
  | 'TITLE'
  | 'ERROR'
  | 'MARKDOWN'
  | 'TEXT'
  | 'RICH_TEXT'
  | 'HTML'
  | 'METRIC_CARD'
  | 'CUSTOM_METRIC_CARD'
  | 'TAB_TABLE'
  | 'FEE_FORM'
  | 'REFERENCES';

// 定义 renderOutput 参数接口
interface RenderOutputParams {
  content: any;
  type: OutputType;
  taskType?: string;
  recommendOrder?: number;
  [key: string]: any;
}

/**
 * showGuess: 默认展示。Tip：非标注页面，展示【猜你想问】；或者当前会话是最后一个，展示【猜你想问】；其他情况不展示
 * isAnnotation：是否是标注页面（申通内外-标注中心）
 * @param props 
 * @returns 
 */
const Answer = (props) => {
  const { outputList, recommendQuestionList, recommendTitle, isTestChat, hitKnowledgeInfo, scrollToBottom, askQuestion,
    finishReason, input: question, actionList, thoughtProgress, thoughtProgressFinished, showGuess = true, outputStatus,
    replaceSession, buttons, contentListBoxStyle, isAnnotation = false,
  } = props || {};
  const [contentList, setContentList] = useState<any[]>([]);
  const { setQuickFormInfo } = useChatStore();

  // 在回答里再次发起提问
  const onSubmit = useCallback((content, extend, ...args) => askQuestion({ content, extend }, ...args), [askQuestion]);

  // 参考知识库列表
  const hitKnowledgeList = useMemo(() => {
    return [].concat(
      hitKnowledgeInfo?.docList || [],
      (hitKnowledgeInfo?.knowledgeQuestionList || [])?.map(({  questionId, previewPcUrl, docTempUrl, questionContent, fullCategoryName }) => ({
        docId: questionId,
        docName: questionContent,
        previewPcUrl,
        docTempUrl,
        type: 'knowledgeQuestion',
        fullCategoryName,
      }))
    );
  }, [hitKnowledgeInfo]);

  // 优先 thoughtProgress.actionList
  const reasoningActionList = useMemo(() => {
    if (thoughtProgress && Array.isArray(thoughtProgress.actionList)) {
      return thoughtProgress.actionList;
    }
    if (Array.isArray(actionList)) {
      return actionList;
    }
    return [];
  }, [thoughtProgress, actionList]);

  const renderOutPut = useCallback(({ content, type, taskType, title, description, ...rest }: RenderOutputParams) => {
    if (type === 'TABLE' && content.queryColumns) {
      type = 'LLM_TABLE';
    }
    if (type === 'TABLE_CHART') {
      // 自动判断 content 是否为 JSON 字符串，如果是则转为对象
      if (typeof content === 'string') {
        try {
          // 只处理可能的 JSON 字符串（以 { 或 [ 开头）
          const trimmed = content.trim();
          if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            content = JSON.parse(trimmed);
          }
        } catch (e) {
          // 解析失败则保持原样
        }
      }
      type = 'TABLE';
    }
    switch (type) {
      case 'LOADING':
        return <ChatLoading content={content} />;
      case 'TABLE':
        // 表格组件：表格组件的宽度由表格组件内部计算，不需要设置maxWidth；
        // 所以后端会根据列的多少设置是否使用“TABLE”类型，列少的就使用TABLE（配置里不设置width），
        // 列多的使用“DATA_JSON”类型里的表格（配置里设置width）
        if (title || description) {
          return <CTitle title={title} description={description}>
            <CTable content={content} />
          </CTitle>
        }
        return <CTable content={content} showTableTitle />;
      case 'DATA_JSON':
        if (title || description) {
          return <CTitle title={title} description={description}>
            <CBi content={content} question={question} />
          </CTitle>
        }
        return <CBi content={content} question={question} />;
      case 'LINK':
        return <CLink {...content} />;
      case 'BUTTON':
        return <CButton content={content} askQuestion={onSubmit} isHistory={props.isHistory} />;
      case 'FORM':
        return <CForm 
          content={content} 
          taskType={taskType || props.taskType} 
          askQuestion={onSubmit} 
          isHistory={props.isHistory} 
          disabled={props.isHistory ||!showGuess} // 历史会话禁用表单，非当前会话是最后一个禁用表单
          formContentStyle={{ padding: '0' }} 
          submitBtnsStyle={{ padding: '12px 0' }} 
          outputStatus={outputStatus}
          replaceSession={(data) => replaceSession?.(props.requestId, { ...data, outputStatus, })}
          requestId={props.requestId}
        />;
      case 'LIST':
        return <CList content={content} />;
      case 'QA':
      case 'RECOMMEND':
        return <CRecommend content={content} askQuestion={onSubmit} taskType={taskType} recommendOrder={rest.recommendOrder} />
      case 'RECOMMENDTITLE':
        // 猜您还想咨询以下问题：标题
        return (!showGuess ? null :
          <div key="recommend-title" className={styles.recommendTitle}>
            <CMarkdown style={{ color: '#4E5969', fontSize: '12px'  }} content={content || question} requestId={props.requestId} onClick={Global.jumpTo} />
          </div>
        );
      case 'GUESS':
        // 猜您还想咨询以下问题：内容
        return (!showGuess ? null :
          <div key={`guess-${content}`} style={{ display: 'flex', marginTop: '8px', gap: '10px' }}>
            <div className={styles.guessItem} onClick={() => {
              if (rest.triggerFormInfo) {
                setQuickFormInfo({ ...rest.triggerFormInfo, taskType: props.taskType });
              } else {
                onSubmit(content, rest.extend);
              }
            }}>
              {content}
            </div>
          </div>
        );
      // 新增 @sto/sto-charts 图表类型
      case 'LINE_CHART':
      case 'BAR_CHART':
      case 'PIE_CHART':
      case 'DOUGHNUT_CHART':
      // case 'TABLE_CHART':
        if (title || description) {
          return <CTitle title={title} description={description}>
            <StoChart content={content} type={type} question={question} />
          </CTitle>
        }
        return <StoChart content={content} type={type} question={question} />;
      case 'ERROR':
      case 'MARKDOWN':
      case "TEXT":
      case "RICH_TEXT":
      case "HTML":
        return <CMarkdown content={content || question} requestId={props.requestId} outputStatus={outputStatus} />;
      case 'METRIC_CARD':
        if (title || description) {
          return <CTitle title={title} description={description}>
            <CMetricCard content={content} question={question} requestId={props.requestId} />
          </CTitle>
        }
        return <CMetricCard content={content} question={question} requestId={props.requestId}/>;
      case 'CUSTOM_METRIC_CARD':
        if (title || description) {
          return <CTitle title={title} description={description}>
            <CCustomMetricCard content={content} question={question} requestId={props.requestId} />
          </CTitle>
        }
        return <CCustomMetricCard content={content} question={question} requestId={props.requestId}/>;
      case 'TAB_TABLE':
        // 可切换表格组件（政策费率功能用，已迁移到政策主应用项目里）
        if (title || description) {
          return <CTitle title={title} description={description}>
            <SwitchCTable content={content} />
          </CTitle>
        }
        return <SwitchCTable content={content} />;
      case 'FEE_FORM':
        // 费率表单（政策费率功能用，已迁移到政策主应用项目里）
        return <FeeForm content={content} />;
      case 'REFERENCES':
        // 参考资料
        return <CReferences content={content} requestId={props.requestId} />;
      default:
        // 检查是否有自定义组件
        const CustomComponent = Global.components[type];
        if (CustomComponent) {
          return <CustomComponent content={content} question={question} askQuestion={onSubmit} {...rest} />;
        }
        // 默认使用 Markdown 渲染
        if (typeof content === 'string') {
          return <CMarkdown content={content || question} requestId={props.requestId} outputStatus={outputStatus} />;
        }
        console.error('ERROR: 未能匹配到组件', content, type);
        // 如果 content 是对象，则直接返回
        return <span>未能匹配到组件</span>;
    }
  }, [onSubmit, props.isHistory, props.taskType, outputStatus]);

  useEffect(() => {
    let list = outputList || [];
    const showRecommend = finishReason === "stop";
    if (showRecommend) {
      const showReferences = props.references || []; // 是否展示参考内容
      if (showReferences && showReferences.length > 0) {
        list = list.concat([
          {
            content: showReferences,
            type: "REFERENCES",
          },
        ]);
      }
      if (props.configComponent) {
        const { questionTemplate, keywordList } = props.configComponent;
        // 换个组织/日期 按钮
        list = list.concat([
          ...(recommendTitle ? [{ content: recommendTitle, type: "RECOMMENDTITLE" }] : []),
          {
            content: '换个组织/日期',
            type: "GUESS",
            triggerFormInfo: {
              title: '换个组织/日期',
              schema: keywordList,
              questionTemplate,
              taskType: props.taskType,
            },
          },
          ...(recommendQuestionList || []).map((item, index) => ({
            ...item,
            content: item.question,
            recommendOrder: index,
            type: "GUESS",
          })),
        ]);
      } else {
        list = list.concat([
          ...(recommendTitle ? [{ content: recommendTitle, type: "RECOMMENDTITLE" }] : []),
          ...(recommendQuestionList || []).map((item, index) => ({
            ...item,
            content: item.question,
            recommendOrder: index,
            type: "GUESS",
          })),
        ]);
      }
    }

    // 判断是否需要前端做流式渲染
    // const isFrontendStream = outputList?.every?.((item) => !item.isStreamOut && 
    //   ['ERROR', 'MARKDOWN', "TEXT", "RICH_TEXT", 'HTML'].includes(item.type));
    // console.log(isFrontendStream, 'isFrontendStream', outputList);
    // if (isFrontendStream && !props.isHistory) {
    //   // 前端流式渲染
    //   streamText(
    //     list,
    //     (v) => {
    //       setContentList(v);
    //       if (scrollToBottom) scrollToBottom();
    //     },
    //     () => {
    //       if (scrollToBottom) scrollToBottom();
    //     }
    //   );
    // } else {
    //   // 直接展示结果后端输出的结果（后端流式输出）
    //   setContentList(list);
    // }

    setContentList(list);
  }, [outputList, recommendQuestionList, recommendTitle, scrollToBottom, finishReason]);

  // 判断是否有推理过程或内容输出（每次渲染都重新计算）
  const hasReasoning = reasoningActionList && reasoningActionList.length > 0;
  const hasContent = contentList && contentList.length > 0;

  return (
    <div className={styles.answer}>
      <div className={styles.card}>
        {!(hasReasoning || hasContent) ? null : (
          <div className={styles.cardContent}>
            <HitKnowledge list={hitKnowledgeList } isTestChat={isTestChat} isAnnotation={isAnnotation} />
            {/* 【推理过程】 */}
            {hasReasoning && <React.Fragment>
              <ReasoningProcess
                actionList={reasoningActionList} 
                taskList={thoughtProgress?.taskList}
                thoughtProgressFinished={thoughtProgressFinished}
              />
            </React.Fragment>}
            {/* 【内容输出】 */}
            {hasContent && contentList.length && <div 
              className={contentList.some(item => item.type === 'LOADING') ? styles.contentListBoxLoading : styles.contentListBox}
              style={contentListBoxStyle}
            >
              {hasContent && contentList.map((item, index) => (
                <div key={`content-${index}`}>
                  {renderOutPut(item)}
                </div>
              ))}
              {/* 按钮组和点赞【评价】 */}
              {outputStatus === 'FINISHED' && <CnBox direction="row" align="center" justify="space-between">
                <Operation buttons={buttons} requestId={props.requestId} />
                <Appraise {...props} />
              </CnBox>}
            </div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(Answer);

